import { useState } from "preact/hooks";
import type { Contact, LostReason, Opportunity } from "../../types";
import { PRODUCT_CATALOG, SALES_OWNERS } from "../../types";
import { Modal } from "../ui/Modal";
import { store } from "../../store/store";
import { toast } from "../shell/Toast";
import { canMarkWon } from "../../lib/businessRules";

const LOST_REASONS: LostReason[] = ["Price", "Competitor", "No budget", "No response", "Timing", "Other"];

export function MarkWonModal({ opportunity, onClose, onDone }: { opportunity: Opportunity; onClose: () => void; onDone: () => void }) {
  const [finalValue, setFinalValue] = useState(String(opportunity.value ?? ""));
  const [productId, setProductId] = useState(opportunity.productId ?? "");
  const [contractStartDate, setContractStartDate] = useState("");
  const [errors, setErrors] = useState<string[]>([]);

  function submit(e: Event) {
    e.preventDefault();
    const check = canMarkWon(opportunity, finalValue ? Number(finalValue) : undefined, productId || undefined, contractStartDate || undefined);
    if (!check.allowed) {
      setErrors(check.reasons);
      return;
    }
    store.markWon(opportunity.id, Number(finalValue), productId, new Date(contractStartDate).toISOString());
    toast.success(`${opportunity.name} marked as Won.`);
    onDone();
  }

  return (
    <Modal title="Mark as Won" onClose={onClose}>
      <form class="form" onSubmit={submit}>
        {errors.length > 0 && (
          <ul class="form-error-list">
            {errors.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        )}
        <label>
          Final value (€)
          <input
            type="number"
            min="0"
            data-ai-id="final-value"
            value={finalValue}
            onInput={(e) => setFinalValue((e.target as HTMLInputElement).value)}
          />
        </label>
        <label>
          Plan / service
          <select data-ai-id="plan-service" value={productId} onChange={(e) => setProductId((e.target as HTMLSelectElement).value)}>
            <option value="">Not selected</option>
            {PRODUCT_CATALOG.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Contract start date
          <input
            type="date"
            data-ai-id="contract-start-date"
            value={contractStartDate}
            onInput={(e) => setContractStartDate((e.target as HTMLInputElement).value)}
          />
        </label>
        <div class="form-actions">
          <button type="button" class="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" class="btn btn--primary" data-ai-id="submit-mark-opportunity-won">
            Mark as Won
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function MarkLostModal({ opportunity, onClose, onDone }: { opportunity: Opportunity; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState<LostReason | "">("");
  const [error, setError] = useState<string | null>(null);

  function submit(e: Event) {
    e.preventDefault();
    if (!reason) {
      setError("A lost reason must be selected.");
      return;
    }
    store.markLost(opportunity.id, reason);
    toast.success(`${opportunity.name} marked as Lost.`);
    onDone();
  }

  return (
    <Modal title="Mark as Lost" onClose={onClose} width={420}>
      <form class="form" onSubmit={submit}>
        {error && <p class="form-error">{error}</p>}
        <label>
          Lost reason
          <select data-ai-id="lost-reason" value={reason} onChange={(e) => setReason((e.target as HTMLSelectElement).value as LostReason)}>
            <option value="">Select a reason</option>
            {LOST_REASONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <div class="form-actions">
          <button type="button" class="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" class="btn btn--danger" data-ai-id="submit-mark-opportunity-lost">
            Mark as Lost
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function AddActivityModal({ opportunity, onClose, onDone }: { opportunity: Opportunity; onClose: () => void; onDone: () => void }) {
  const [type, setType] = useState<"call" | "email" | "meeting" | "note" | "task">("call");
  const [content, setContent] = useState("");
  const [author, setAuthor] = useState(opportunity.salesOwner);
  const [error, setError] = useState<string | null>(null);

  function submit(e: Event) {
    e.preventDefault();
    if (!content.trim()) {
      setError("Activity description cannot be empty.");
      return;
    }
    store.addActivity(opportunity.id, type, content.trim(), author);
    toast.success("Activity added.");
    onDone();
  }

  return (
    <Modal title="Add Activity" onClose={onClose} width={440}>
      <form class="form" onSubmit={submit}>
        {error && <p class="form-error">{error}</p>}
        <label>
          Type
          <select data-ai-id="activity-type" value={type} onChange={(e) => setType((e.target as HTMLSelectElement).value as typeof type)}>
            <option value="call">Call</option>
            <option value="email">Email</option>
            <option value="meeting">Meeting</option>
            <option value="note">Note</option>
            <option value="task">Task</option>
          </select>
        </label>
        <label>
          Author
          <select data-ai-id="activity-author" value={author} onChange={(e) => setAuthor((e.target as HTMLSelectElement).value)}>
            {SALES_OWNERS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        <label>
          Description
          <textarea data-ai-id="activity-content" value={content} onInput={(e) => setContent((e.target as HTMLTextAreaElement).value)} />
        </label>
        <div class="form-actions">
          <button type="button" class="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" class="btn btn--primary" data-ai-id="submit-add-activity">
            Add Activity
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ProposalFormModal({ opportunity, onClose, onDone }: { opportunity: Opportunity; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState(String(opportunity.value ?? ""));
  const [discountPercent, setDiscountPercent] = useState("0");
  const [expirationDate, setExpirationDate] = useState("");
  const [status, setStatus] = useState<"Draft" | "Sent">("Sent");
  const [error, setError] = useState<string | null>(null);

  function submit(e: Event) {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) {
      setError("Proposal amount must be greater than zero.");
      return;
    }
    if (!expirationDate) {
      setError("Expiration date is required.");
      return;
    }
    const proposalNumber = `PRO-${new Date().getFullYear()}-${Math.floor(Math.random() * 900 + 100)}`;
    store.addProposal({
      opportunityId: opportunity.id,
      proposalNumber,
      amount: Number(amount),
      discountPercent: Number(discountPercent) || 0,
      expirationDate: new Date(expirationDate).toISOString(),
      status,
      sentDate: status === "Sent" ? new Date().toISOString() : undefined,
    });
    toast.success("Proposal created.");
    onDone();
  }

  const discountHigh = Number(discountPercent) > 20;

  return (
    <Modal title="Create Proposal" onClose={onClose} width={440}>
      <form class="form" onSubmit={submit}>
        {error && <p class="form-error">{error}</p>}
        <label>
          Amount (€)
          <input
            type="number"
            min="0"
            data-ai-id="proposal-amount"
            value={amount}
            onInput={(e) => setAmount((e.target as HTMLInputElement).value)}
          />
        </label>
        <label>
          Discount (%)
          <input
            type="number"
            min="0"
            max="100"
            data-ai-id="discount"
            value={discountPercent}
            onInput={(e) => setDiscountPercent((e.target as HTMLInputElement).value)}
          />
        </label>
        {discountHigh && <p class="form-warning">A discount above 20% will require manager approval before it can be used to move this opportunity to Negotiation.</p>}
        <label>
          Expiration date
          <input
            type="date"
            data-ai-id="proposal-expiration"
            value={expirationDate}
            onInput={(e) => setExpirationDate((e.target as HTMLInputElement).value)}
          />
        </label>
        <label>
          Status
          <select data-ai-id="proposal-status" value={status} onChange={(e) => setStatus((e.target as HTMLSelectElement).value as typeof status)}>
            <option value="Draft">Draft</option>
            <option value="Sent">Sent</option>
          </select>
        </label>
        <div class="form-actions">
          <button type="button" class="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" class="btn btn--primary" data-ai-id="submit-create-proposal">
            Create Proposal
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ChangeOwnerModal({ opportunity, onClose, onDone }: { opportunity: Opportunity; onClose: () => void; onDone: () => void }) {
  const [owner, setOwner] = useState(opportunity.salesOwner);

  function submit(e: Event) {
    e.preventDefault();
    store.changeOwner(opportunity.id, owner);
    toast.success("Owner updated.");
    onDone();
  }

  return (
    <Modal title="Change Owner" onClose={onClose} width={380}>
      <form class="form" onSubmit={submit}>
        <label>
          Sales owner
          <select data-ai-id="sales-owner" value={owner} onChange={(e) => setOwner((e.target as HTMLSelectElement).value)}>
            {SALES_OWNERS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        <div class="form-actions">
          <button type="button" class="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" class="btn btn--primary" data-ai-id="submit-change-owner">
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ScheduleFollowUpModal({ opportunity, onClose, onDone }: { opportunity: Opportunity; onClose: () => void; onDone: () => void }) {
  const [dueDate, setDueDate] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  function submit(e: Event) {
    e.preventDefault();
    if (!dueDate) {
      setError("A follow-up date is required.");
      return;
    }
    store.scheduleFollowUp(opportunity.id, new Date(dueDate).toISOString(), note.trim() || "Follow up with the customer.", opportunity.salesOwner);
    toast.success("Follow-up scheduled.");
    onDone();
  }

  return (
    <Modal title="Schedule Follow-up" onClose={onClose} width={400}>
      <form class="form" onSubmit={submit}>
        {error && <p class="form-error">{error}</p>}
        <label>
          Follow-up date
          <input type="date" data-ai-id="follow-up-date" value={dueDate} onInput={(e) => setDueDate((e.target as HTMLInputElement).value)} />
        </label>
        <label>
          Note
          <textarea data-ai-id="follow-up-note" value={note} onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)} />
        </label>
        <div class="form-actions">
          <button type="button" class="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" class="btn btn--primary" data-ai-id="submit-schedule-follow-up">
            Schedule
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function EditOpportunityModal({
  opportunity,
  contacts,
  onClose,
  onDone,
}: {
  opportunity: Opportunity;
  contacts: Contact[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(opportunity.name);
  const [value, setValue] = useState(String(opportunity.value ?? ""));
  const [probability, setProbability] = useState(String(opportunity.probability));
  const [contactId, setContactId] = useState(opportunity.primaryContactId ?? "");
  const [closeDate, setCloseDate] = useState(opportunity.expectedCloseDate ? opportunity.expectedCloseDate.slice(0, 10) : "");
  const [competitor, setCompetitor] = useState(opportunity.competitor ?? "");
  const [tags, setTags] = useState(opportunity.tags.join(", "));
  const [internalNotes, setInternalNotes] = useState(opportunity.internalNotes ?? "");
  const [description, setDescription] = useState(opportunity.description);
  const [error, setError] = useState<string | null>(null);

  function submit(e: Event) {
    e.preventDefault();
    if (!name.trim()) {
      setError("Opportunity name is required.");
      return;
    }
    store.updateOpportunity(opportunity.id, {
      name: name.trim(),
      value: value ? Number(value) : undefined,
      probability: Number(probability) || 0,
      primaryContactId: contactId || undefined,
      expectedCloseDate: closeDate ? new Date(closeDate).toISOString() : undefined,
      competitor: competitor.trim() || undefined,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      internalNotes: internalNotes.trim() || undefined,
      description: description.trim(),
    });
    toast.success("Opportunity updated.");
    onDone();
  }

  return (
    <Modal title="Edit Opportunity" onClose={onClose} width={520}>
      <form class="form" onSubmit={submit}>
        {error && <p class="form-error">{error}</p>}
        <label>
          Opportunity name
          <input data-ai-id="opportunity-name" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
        </label>
        <label>
          Primary contact
          <select data-ai-id="primary-contact" value={contactId} onChange={(e) => setContactId((e.target as HTMLSelectElement).value)}>
            <option value="">Not selected</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — {c.role}
              </option>
            ))}
          </select>
        </label>
        <div class="form-row">
          <label>
            Estimated value (€)
            <input
              type="number"
              min="0"
              data-ai-id="estimated-value"
              value={value}
              onInput={(e) => setValue((e.target as HTMLInputElement).value)}
            />
          </label>
          <label>
            Probability (%)
            <input
              type="number"
              min="0"
              max="100"
              data-ai-id="probability"
              value={probability}
              onInput={(e) => setProbability((e.target as HTMLInputElement).value)}
            />
          </label>
        </div>
        <label>
          Expected close date
          <input
            type="date"
            data-ai-id="expected-close-date"
            value={closeDate}
            onInput={(e) => setCloseDate((e.target as HTMLInputElement).value)}
          />
        </label>
        <label>
          Main competitor
          <input data-ai-id="competitor" value={competitor} onInput={(e) => setCompetitor((e.target as HTMLInputElement).value)} />
        </label>
        <label>
          Tags (comma separated)
          <input data-ai-id="tags" value={tags} onInput={(e) => setTags((e.target as HTMLInputElement).value)} />
        </label>
        <label>
          Description
          <textarea data-ai-id="description" value={description} onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)} />
        </label>
        <label>
          Internal notes
          <textarea data-ai-id="internal-notes" value={internalNotes} onInput={(e) => setInternalNotes((e.target as HTMLTextAreaElement).value)} />
        </label>
        <div class="form-actions">
          <button type="button" class="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" class="btn btn--primary" data-ai-id="submit-edit-opportunity">
            Save Changes
          </button>
        </div>
      </form>
    </Modal>
  );
}
